import { Link, router } from "@inertiajs/react";
import type Producer from "@/types/serializers/Producer";
import { useFlashToast } from "@/hooks/useFlashToast";
import { useIndexNavigation, useSyncedFilter } from "@/hooks/useIndexNavigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  X,
} from "lucide-react";

type Props = {
  producers: Producer[];
  total: number;
  page: { size: number; number: number };
  totalPages: number;
  flash?: { notice?: string; alert?: string };
};

function SortIcon({ direction }: { direction: "asc" | "desc" | null }) {
  if (direction === "asc") return <ArrowUp className="size-[11.2px]" />;
  if (direction === "desc") return <ArrowDown className="size-[11.2px]" />;
  return <ArrowUpDown className="size-[11.2px] text-muted-foreground" />;
}

export default function ProducersIndex({
  producers,
  total,
  page,
  totalPages,
  flash,
}: Props) {
  useFlashToast(flash);
  const navigation = useIndexNavigation("/producers", {
    only: ["producers", "total", "totalPages", "page"],
  });
  const { navigate, handleSort, sortDirection, pagination } = navigation;
  const [filterName, setFilterName] = useSyncedFilter("filter_name", navigation);
  const [filterCity, setFilterCity] = useSyncedFilter("filter_city", navigation);

  function clearFilters() {
    navigate({ filter_name: null, filter_city: null, offset: null });
  }

  const hasFilters = filterName || filterCity;
  const { currentPage, canPrev, canNext } = pagination(page, totalPages);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Producers</h1>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? "producer" : "producers"} total
          </p>
        </div>
        <Button asChild>
          <Link href="/producers/new">
            <Plus className="size-4" />
            New Producer
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Input
            placeholder="Filter by name…"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="pl-[40px]"
            style={{ paddingLeft: "30px" }}
          />
          <Search className="pointer-events-none absolute left-2.5 top-0 h-9 size-[16.7px] text-muted-foreground" />
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Input
            placeholder="Filter by city…"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="pl-[40px]"
            style={{ paddingLeft: "30px" }}
          />
          <Search className="pointer-events-none absolute left-2.5 top-0 h-9 size-[16.7px] text-muted-foreground" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="size-4" />
            Clear
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("id")}
                >
                  ID <SortIcon direction={sortDirection("id")} />
                </button>
              </TableHead>
              <TableHead>
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("name")}
                >
                  Name <SortIcon direction={sortDirection("name")} />
                </button>
              </TableHead>
              <TableHead className="hidden md:table-cell">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("city")}
                >
                  City <SortIcon direction={sortDirection("city")} />
                </button>
              </TableHead>
              <TableHead className="hidden lg:table-cell">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("state")}
                >
                  State <SortIcon direction={sortDirection("state")} />
                </button>
              </TableHead>
              <TableHead className="hidden lg:table-cell">Country</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {producers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No producers found.
                </TableCell>
              </TableRow>
            ) : (
              producers.map((producer) => (
                <TableRow key={producer.id}>
                  <TableCell className="font-mono text-muted-foreground">
                    {producer.id}
                  </TableCell>
                  <TableCell className="font-medium">
                    <Link
                      href={`/producers/${producer.id}`}
                      className="hover:underline"
                    >
                      {producer.name}
                    </Link>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {producer.city || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {producer.state || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {producer.country?.name || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/producers/${producer.id}`}>View</Link>
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/producers/${producer.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm("Are you sure you want to delete this producer?"))
                            router.delete(`/producers/${producer.id}`);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={String(page.size)}
              onValueChange={(value) =>
                navigate({ page_size: value, offset: null })
              }
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 15, 25].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!canPrev}
                onClick={() =>
                  navigate({ offset: String(page.number - page.size) })
                }
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!canNext}
                onClick={() =>
                  navigate({ offset: String(page.number + page.size) })
                }
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
